import { createContext, useCallback, useContext, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useCan } from "@/lib/auth";
import { qk } from "@/lib/query/keys";
import { awaitingTotal, countByBoard, indexWaiting, sortWaiting } from "@/lib/waiting";
import type { WaitingCard, WaitingChatQuestion, WaitingReviewCard } from "@/lib/types";

export interface WaitingState {
  /** False below admin: the list is then always empty and nothing about it renders. */
  enabled: boolean;
  /** Oldest first. */
  cards: WaitingCard[];
  /** Open questions asked in an agent chat about no listed card, oldest first. */
  questions: WaitingChatQuestion[];
  /** Rows on the Awaiting page: the cards plus the chat questions — the badge. */
  total: number;
  /** Cards in a review column, oldest first. Counted apart: forty reviews must not drown one question. */
  reviews: WaitingReviewCard[];
  byTask: Map<string, WaitingCard>;
  byBoard: Map<string, number>;
  loaded: boolean;
  /** Re-reads the list at once — after an answer, rather than waiting on the feed. */
  refresh: () => Promise<void>;
}

const EMPTY: WaitingState = {
  enabled: false,
  cards: [],
  questions: [],
  total: 0,
  reviews: [],
  byTask: new Map(),
  byBoard: new Map(),
  loaded: false,
  refresh: async () => undefined,
};

export const WaitingContext = createContext<WaitingState>(EMPTY);

/** The shared list, read by cards, the board header, the navigation and the task sheet. */
export const useWaiting = (): WaitingState => useContext(WaitingContext);

const NONE: never[] = [];

/** Stable, so the query memoises its result and the context does not change every render. */
const shapeWaiting = (data: Awaited<ReturnType<typeof api.waiting>>) => ({
  cards: sortWaiting(data.cards),
  questions: data.questions ?? NONE,
  reviews: data.reviews ?? NONE,
});

/**
 * One cached `GET /api/waiting` for the whole app. The live feed invalidates it
 * when a write touches what it is built from (`awaiting` in `invalidation.ts`),
 * and an agent's question opening or closing — which lands in a transcript, not
 * always in a revision — invalidates it too. Only an admin fetches: a question is
 * what an agent said, which only they may read, so for anyone else the list
 * stays empty and nothing waiting-related shows.
 */
export function useWaitingState(): WaitingState {
  const enabled = useCan("admin");
  const client = useQueryClient();
  const query = useQuery({
    queryKey: qk.awaiting(),
    queryFn: () => api.waiting(),
    enabled,
    select: shapeWaiting,
  });
  const refresh = useCallback(() => client.invalidateQueries({ queryKey: qk.awaiting() }), [client]);

  const cards = (enabled && query.data?.cards) || NONE;
  const questions = (enabled && query.data?.questions) || NONE;
  const reviews = (enabled && query.data?.reviews) || NONE;
  const loaded = query.isFetched || query.data !== undefined;

  return useMemo(
    () => ({
      enabled,
      cards,
      questions,
      total: awaitingTotal({ cards, questions }),
      reviews,
      byTask: indexWaiting(cards),
      byBoard: countByBoard(cards),
      loaded,
      refresh,
    }),
    [enabled, cards, questions, reviews, loaded, refresh],
  );
}
